import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDashboardNotifications } from "../../context/DashboardNotificationsContext";
import {
  getCategoryGradient,
  getCategoryMeta,
  getCategoryWash,
  isCriticalPriority,
} from "../../data/DashboardNotifications/notificationStyles";

const NotificationModal = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const { activeModal, closeModal } = useDashboardNotifications();

  if (!activeModal) return null;

  const meta = getCategoryMeta(activeModal.category);
  const critical = isCriticalPriority(activeModal.priority);

  const handleClose = (event, reason) => {
    if (
      !activeModal.isDismissible &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    ) {
      return;
    }
    closeModal(activeModal.id, { dismissForever: activeModal.isDismissible });
  };

  const handleCta = () => {
    closeModal(activeModal.id, { dismissForever: activeModal.isDismissible });
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      role="dialog"
      aria-labelledby="dashboard-message-modal-title"
      transitionDuration={300}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: critical
            ? `0 0 0 2px ${meta.accent}, 0 20px 60px ${meta.accent}55`
            : `0 20px 60px ${meta.accent}33`,
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          background: getCategoryWash(meta, isDark),
          borderBottom: `1px solid ${meta.accent}33`,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            background: getCategoryGradient(meta, isDark),
            boxShadow: `0 4px 16px ${meta.accent}66`,
            ...(critical && !reduceMotion
              ? {
                  animation: "dashboardModalPulseRing 1.6s ease-in-out infinite",
                  "@keyframes dashboardModalPulseRing": {
                    "0%, 100%": { boxShadow: `0 0 0 0 ${meta.accent}88` },
                    "50%": { boxShadow: `0 0 0 8px ${meta.accent}00` },
                  },
                }
              : {}),
          }}
        >
          <meta.Icon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, pt: 0.5 }}>
          <Typography
            variant="overline"
            sx={{ color: meta.accent, fontWeight: 700, letterSpacing: 1 }}
          >
            {meta.label}
          </Typography>
          <Typography
            id="dashboard-message-modal-title"
            variant="h6"
            sx={{ fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a" }}
          >
            {activeModal.title}
          </Typography>
        </Box>
        {activeModal.isDismissible && (
          <IconButton
            aria-label={`Dismiss notification: ${activeModal.title}`}
            onClick={() =>
              closeModal(activeModal.id, { dismissForever: true })
            }
            sx={{ mt: -0.5, mr: -1 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
          {activeModal.body}
        </Typography>
      </DialogContent>
      {activeModal.ctaLabel && activeModal.ctaUrl && (
        <DialogActions sx={{ justifyContent: "center", pb: 3.5, pt: 0.5 }}>
          <Button
            variant="contained"
            href={activeModal.ctaUrl}
            onClick={handleCta}
            sx={{
              background: getCategoryGradient(meta, isDark),
              textTransform: "none",
              fontWeight: 700,
              px: 4,
              py: 1,
              borderRadius: "10px",
              boxShadow: `0 4px 14px ${meta.accent}66`,
              "&:hover": {
                filter: "brightness(1.08)",
                boxShadow: `0 6px 20px ${meta.accent}77`,
              },
            }}
          >
            {activeModal.ctaLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default NotificationModal;
